import type { MarkerDef } from './types'

/**
 * Marcadores laboratoriais da metodologia Mãe Nutrida para HOMEM.
 *
 * Fontes:
 *   - `Exames Laboratoriais - Homens.xlsx` → lista `homem`
 *   - `Exames Laboratoriais - Espermograma.xlsx` → lista `espermograma`
 *
 * As duas listas ficam SEPARADAS para que a UI possa exibir um divisor
 * visual entre "Espermograma" e os demais exames de saúde do homem.
 */
export const HOMEM_MARKERS: { espermograma: MarkerDef[]; homem: MarkerDef[] } = {
  espermograma: [
    {
      id: 'volume-do-semen',
      name: 'Volume do sêmen',
      group: 'Espermograma',
      ranges: {
        default: { min: 1.5, display: '> 1,5ml', unit: 'ml' },
      },
    },
    {
      id: 'concentracao-do-espermatozoide-ml',
      name: 'Concentração do espermatozoide /ml',
      group: 'Espermograma',
      ranges: {
        default: { min: 66, display: '> 66 milhões' },
      },
    },
    {
      id: 'concentracao-do-espermatozoide-total',
      name: 'Concentração do espermatozoide total',
      group: 'Espermograma',
      ranges: {
        default: { min: 210, display: '> 210 milhões' },
      },
    },
    {
      id: 'motilidade-total',
      name: 'Motilidade total',
      group: 'Espermograma',
      ranges: {
        default: { min: 42, display: '> 42%', unit: '%' },
      },
    },
    {
      id: 'motilidade-progressiva',
      name: 'Motilidade progressiva (A+B)',
      group: 'Espermograma',
      ranges: {
        default: { min: 30, display: '> 30%', unit: '%' },
      },
    },
    {
      id: 'motilidade-nao-progressiva-1',
      name: 'Motilidade não progressiva 1',
      group: 'Espermograma',
      ranges: {
        default: { max: 5, display: '< 5%', unit: '%' },
      },
    },
    {
      id: 'espermatozoides-imoveis',
      name: 'Espermatozoides imóveis',
      group: 'Espermograma',
      ranges: {
        default: { max: 39, display: '< 39%', unit: '%' },
      },
    },
    {
      id: 'vitalidade',
      name: 'Vitalidade',
      group: 'Espermograma',
      ranges: {
        default: { min: 79, display: '> 79%', unit: '%' },
      },
    },
    {
      id: 'formas-normais',
      name: 'Formas normais',
      group: 'Espermograma',
      ranges: {
        default: { min: 15, display: '>15%', unit: '%' },
      },
    },
    {
      id: 'morfologia-de-kruger',
      name: 'Morfologia de Kruger',
      group: 'Espermograma',
      ranges: {
        default: { min: 4, display: '>4%', unit: '%' },
      },
    },
    {
      id: 'liquefacao',
      name: 'Liquefação',
      group: 'Espermograma',
      ranges: {
        default: { display: 'completa até 30 minutos', unit: 'minutos' },
      },
    },
    {
      id: 'viscosidade',
      name: 'Viscosidade',
      group: 'Espermograma',
      ranges: {
        default: { display: 'Normal' },
      },
    },
    {
      id: 'ph',
      name: 'PH',
      group: 'Espermograma',
      ranges: {
        default: { min: 7.2, display: '>7,2' },
      },
    },
    {
      id: 'esperm-leucocitos',
      name: 'Leucócitos',
      group: 'Espermograma',
      ranges: {
        default: { max: 1, display: '< 1 milhão/ml', unit: 'milhão/ml' },
      },
    },
  ],
  homem: [
    {
      id: 'hemacias',
      name: 'Hemácias',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { display: 'VR do laboratório' },
      },
    },
    {
      id: 'hemoglobina',
      name: 'Hemoglobina',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 14, max: 16, display: '14-16 g/dL', unit: 'g/dL' },
      },
    },
    {
      id: 'hematocrito',
      name: 'Hematócrito',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 42, max: 48, display: '42-48%', unit: '%' },
      },
    },
    {
      id: 'hcm',
      name: 'HCM',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 27, max: 33, display: '27 - 33pg', unit: 'pg' },
      },
    },
    {
      id: 'vcm',
      name: 'VCM',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 88, max: 92, display: '88 - 92fl', unit: 'fl' },
      },
    },
    {
      id: 'chcm',
      name: 'CHCM',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 31, max: 35, display: '31 - 35g/dL', unit: 'g/dL' },
      },
    },
    {
      id: 'rdw',
      name: 'RDW',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 11, max: 16, display: '11 – 16%', unit: '%' },
      },
    },
    {
      id: 'leucocitos',
      name: 'Leucócitos',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 4000, max: 6500, display: '4000 - 6500mm³', unit: 'mm³' },
      },
    },
    {
      id: 'eosinofilos',
      name: 'Eosinófilos',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { max: 1, display: '< 1%', unit: '%' },
      },
    },
    {
      id: 'linfocitos',
      name: 'Linfócitos',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 25, max: 35, display: '25 - 35%', unit: '%' },
      },
    },
    {
      id: 'monocitos',
      name: 'Monócitos',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 3, max: 8, display: '3 - 8 %', unit: '%' },
      },
    },
    {
      id: 'plaquetas',
      name: 'Plaquetas',
      group: 'Eritrograma e Leucograma',
      ranges: {
        default: { min: 250, max: 300, display: '250 mil - 300 mil' },
      },
    },
    {
      id: '25-odd3',
      name: '25 (OD)D3',
      group: 'Nutricional',
      ranges: {
        default: { min: 50, max: 80, display: '50 - 80ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'acido-folico',
      name: 'Ácido fólico',
      group: 'Nutricional',
      ranges: {
        default: { min: 10, max: 17, display: '10 - 17ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'vitamina-b12',
      name: 'Vitamina B12',
      group: 'Nutricional',
      ranges: {
        default: { min: 500, max: 800, display: '500 - 800 pg/mL', unit: 'pg/mL' },
      },
    },
    {
      id: 'homocisteina',
      name: 'Homocisteína',
      group: 'Nutricional',
      ranges: {
        default: { max: 8, display: '< 8 mcmol/L', unit: 'mcmol/L' },
      },
    },
    {
      id: 'zinco-serico',
      name: 'Zinco sérico',
      group: 'Nutricional',
      ranges: {
        default: { min: 90, max: 110, display: '90 - 110 mcg/dL', unit: 'mcg/dL' },
      },
    },
    {
      id: 'cobre-serico',
      name: 'Cobre sérico',
      group: 'Nutricional',
      ranges: {
        default: { min: 80, max: 110, display: '80 a 110mcg/dL', unit: 'mcg/dL' },
      },
    },
    {
      id: 'selenio-serico',
      name: 'Selênio sérico',
      group: 'Nutricional',
      ranges: {
        default: { min: 100, max: 120, display: '100 - 120mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'calcio-ionico',
      name: 'Cálcio iônico',
      group: 'Nutricional',
      ranges: {
        default: { min: 1.1, max: 1.25, display: '1,1 a 1,25 mmol/L', unit: 'mmol/L' },
      },
    },
    {
      id: 'calcio-serico',
      name: 'Cálcio sérico',
      group: 'Nutricional',
      ranges: {
        default: { min: 2.28, max: 2.52, display: '2,28-2,52mmol/L', unit: 'mmol/L' },
      },
    },
    {
      id: 'magnesio-serico',
      name: 'Magnésio sérico',
      group: 'Nutricional',
      ranges: {
        default: { min: 2, max: 2.2, display: '2,0 - 2,2 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'ferritina',
      name: 'Ferritina',
      group: 'Avaliação do ferro',
      obs: 'acompanhar com PCR',
      ranges: {
        default: { min: 70, max: 300, display: '70 - 300ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'pcr',
      name: 'PCR',
      group: 'Avaliação do ferro',
      ranges: {
        default: { max: 1, display: '< 1,0 mg/L', unit: 'mg/L' },
      },
    },
    {
      id: 'chumbo',
      name: 'Chumbo',
      group: 'Metais pesados',
      ranges: {
        default: { max: 3, display: '< 3,0 mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'cadmio',
      name: 'Cádmio',
      group: 'Metais pesados',
      ranges: {
        default: { max: 0.5, display: '< 0,5 mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'aluminio',
      name: 'Alumínio',
      group: 'Metais pesados',
      ranges: {
        default: { max: 2, display: '< 2,0 mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'mercurio',
      name: 'Mercúrio',
      group: 'Metais pesados',
      ranges: {
        default: { max: 1, display: '< 1,0 mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'arsenico',
      name: 'Arsênico',
      group: 'Metais pesados',
      ranges: {
        default: { max: 10, display: '< 10,0 mcg/L', unit: 'mcg/L' },
      },
    },
    {
      id: 'glicemia-jejum',
      name: 'Glicemia jejum',
      group: 'Glicêmico',
      obs: '75-85',
      ranges: {
        default: { max: 85, display: '< 85mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'insulina-jejum',
      name: 'Insulina jejum',
      group: 'Glicêmico',
      ranges: {
        default: { max: 10, display: '< 10µU/mL', unit: 'µU/mL' },
      },
    },
    {
      id: 'glicemia-pos-prandial',
      name: 'Glicemia pós prandial',
      group: 'Glicêmico',
      obs: '2h após almoço',
      ranges: {
        default: { max: 140, display: '< 140mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'insulina-pos-prandial',
      name: 'Insulina pós prandial',
      group: 'Glicêmico',
      obs: '2h após almoço',
      ranges: {
        default: { max: 10, display: '< 10µU/mL', unit: 'µU/mL' },
      },
    },
    {
      id: 'hemoglobina-glicada',
      name: 'Hemoglobina glicada',
      group: 'Glicêmico',
      ranges: {
        default: { max: 5, display: '< 5%', unit: '%' },
      },
    },
    {
      id: 'fsh',
      name: 'FSH',
      group: 'Hormonal',
      ranges: {
        default: { min: 2.5, max: 5, display: '2,5-5,0UI/L', unit: 'UI/L' },
      },
    },
    {
      id: 'lh',
      name: 'LH',
      group: 'Hormonal',
      ranges: {
        default: { min: 4.5, max: 5, display: '4,5-5,0UI/L', unit: 'UI/L' },
      },
    },
    {
      id: 'estradiol',
      name: 'Estradiol',
      group: 'Hormonal',
      ranges: {
        default: { min: 25, max: 30, display: '25-30pg/mL', unit: 'pg/mL' },
      },
    },
    {
      id: 'shbg',
      name: 'SHBG',
      group: 'Hormonal',
      obs: 'próximo a 15',
      ranges: {
        default: { display: '15nmol/L', unit: 'nmol/L' },
      },
    },
    {
      id: 'prolactina',
      name: 'Prolactina',
      group: 'Hormonal',
      ranges: {
        default: { min: 5, max: 14, display: '5-14ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 's-dhea',
      name: 'S-DHEA',
      group: 'Hormonal',
      ranges: {
        default: { min: 400, max: 500, display: '400-500mcg/dL', unit: 'mcg/dL' },
      },
    },
    {
      id: 'testosterona-total',
      name: 'Testosterona total',
      group: 'Hormonal',
      ranges: {
        default: { min: 700, max: 1200, display: '700-1200ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'testosterona-livre',
      name: 'Testosterona livre',
      group: 'Hormonal',
      ranges: {
        default: { min: 15, max: 18, display: '15-18 ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'cortisol',
      name: 'Cortisol (até 8:30h)',
      group: 'Hormonal',
      ranges: {
        default: { min: 15, max: 20, display: '15 - 20 mcg/dL', unit: 'mcg/dL' },
      },
    },
    {
      id: 'tsh',
      name: 'TSH',
      group: 'Tireoide',
      ranges: {
        default: { max: 2.5, display: '< 2,5 UI/mL', unit: 'UI/mL' },
      },
    },
    {
      id: 't3-livre',
      name: 'T3 livre',
      group: 'Tireoide',
      ranges: {
        default: { min: 3, max: 4, display: '3,0 – 4ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 't4-livre',
      name: 'T4 livre',
      group: 'Tireoide',
      ranges: {
        default: { min: 1.1, display: '> 1,1ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 't3-reverso',
      name: 'T3 reverso',
      group: 'Tireoide',
      ranges: {
        default: { min: 0.1, max: 0.25, display: '0,1 - 0,25ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'anti-tireoglobulina',
      name: 'Anti tireoglobulina',
      group: 'Tireoide',
      obs: 'o importante é ser < que o VR',
      ranges: {
        default: { display: '< VR' },
      },
    },
    {
      id: 'anti-tireoperoxidase',
      name: 'Anti tireoperoxidase',
      group: 'Tireoide',
      ranges: {
        default: { display: '< VR' },
      },
    },
    {
      id: 'tireoglobulina',
      name: 'Tireoglobulina',
      group: 'Tireoide',
      ranges: {
        default: { min: 8, max: 13, display: '8 - 13ng/mL', unit: 'ng/mL' },
      },
    },
    {
      id: 'pth',
      name: 'PTH',
      group: 'Tireoide',
      ranges: {
        default: { min: 25, max: 40, display: '25-40pg/mL', unit: 'pg/mL' },
      },
    },
    {
      id: 'colesterol-total',
      name: 'Colesterol total',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { max: 200, display: '< 200 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'colesterol-hdl',
      name: 'Colesterol HDL',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { min: 50, max: 73, display: '50-73 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'colesterol-ldl',
      name: 'Colesterol LDL',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { max: 100, display: '< 100 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'triglicerideos',
      name: 'Triglicerídeos',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { max: 100, display: '< 100 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'acido-urico',
      name: 'Ácido úrico',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { max: 4.9, display: '< 4,9 mg/dL', unit: 'mg/dL' },
      },
    },
    {
      id: 'tgo',
      name: 'TGO',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      },
    },
    {
      id: 'tgp',
      name: 'TGP',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      },
    },
    {
      id: 'ggt',
      name: 'GGT',
      group: 'Lipídico, metabólico e hepático',
      ranges: {
        default: { min: 10, max: 20, display: '10 - 20U/L', unit: 'U/L' },
      },
    },
  ],
}
